/**
 * Email Signup Block Component
 *
 * Handles dynamic padding adjustment for email signup input fields
 * when integrated buttons have variable widths.
 */
class EmailSignupBlock extends HTMLElement {
  constructor() {
    super();
    /** @type {number} */
    this.lastButtonWidth = 0;
    /** @type {ResizeObserver | undefined} */
    this.observer = undefined;

    this.adjustInputPadding = this.adjustInputPadding.bind(this);
  }

  connectedCallback() {
    this.button = this.querySelector(".email-signup__button--integrated");
    this.input = this.querySelector(".email-signup__input");
    this.inputGroup = this.querySelector(".email-signup__input-group");

    const isTextButton =
      this.button &&
      this.button.classList.contains("email-signup__button--text");

    if (this.button && this.input && this.inputGroup && isTextButton) {
      this.adjustInputPadding();

      this.observer = new ResizeObserver(() => {
        this.adjustInputPadding();
      });
      this.observer.observe(this.button);

      if ("fonts" in document) {
        document.fonts.ready.then(() => {
          this.adjustInputPadding();
        });
      }
    }

    // Check for success message and show popup
    this.checkForSuccess();
  }

  disconnectedCallback() {
    if (this.observer) {
      this.observer.disconnect();
    }
  }

  adjustInputPadding() {
    if (!this.button || !this.inputGroup) return;

    const buttonWidth = /** @type {HTMLElement} */ (this.button).offsetWidth;

    if (buttonWidth !== this.lastButtonWidth) {
      this.lastButtonWidth = buttonWidth;
      /** @type {HTMLElement} */ (this.inputGroup).style.setProperty(
        "--button-actual-width",
        `${buttonWidth}px`
      );
    }
  }

  checkForSuccess() {
    const successMessage = this.querySelector(
      ".email-signup__message--success"
    );

    if (successMessage) {
      // Hide the original success message
      successMessage.style.display = "none";
      // Show welcome popup
      this.showWelcomePopup();
    }
  }

  showWelcomePopup() {
    const popup = new EmailSignupPopup();
    popup.show();
  }
}

/**
 * Email Signup Success Popup
 *
 * Shows a Klaviyo-style welcome popup when email signup is successful
 */
class EmailSignupPopup {
  constructor() {
    this.couponCode = "WMCBD-FJT-10";
  }

  show() {
    const dialog = this.createDialog();
    document.body.appendChild(dialog);
    dialog.showModal();
    this.setupHandlers(dialog);
  }

  createDialog() {
    const dialog = document.createElement("dialog");
    dialog.className = "email-signup-popup";
    dialog.innerHTML = `
      <div class="email-signup-popup__content">
        <button class="email-signup-popup__close" aria-label="Close">
          <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
            <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>

        <h2 class="email-signup-popup__title">✈️ Welcome Aboard!</h2>

        <p class="email-signup-popup__message">
          Thank you!<br>
          Use the code below for <strong>10% off</strong><br>your first journey with TRAB.
        </p>

        <div class="email-signup-popup__coupon-container">
          <div class="email-signup-popup__coupon-code">${this.couponCode}</div>
          <button class="email-signup-popup__copy-btn">
            <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
              <rect x="7" y="7" width="10" height="10" rx="1" stroke="currentColor" stroke-width="1.5" fill="none"/>
              <path d="M5 13H4C3.44772 13 3 12.5523 3 12V4C3 3.44772 3.44772 3 4 3H12C12.5523 3 13 3.44772 13 4V5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/>
            </svg>
            Copy Code
          </button>
        </div>

        <div class="email-signup-popup__success-message" style="display: none;">
          <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 16 16" fill="none">
            <circle cx="8" cy="8" r="8" fill="#4CAF50"/>
            <path d="M5 8L7 10L11 6" stroke="white" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Coupon applied to checkout!
        </div>

        <p class="email-signup-popup__footer">
          We've also sent this code to your inbox,<br>
          just in case you want to use it later 😊
        </p>
      </div>
    `;

    this.addStyles();
    return dialog;
  }

  setupHandlers(dialog) {
    const closeButton = dialog.querySelector(".email-signup-popup__close");
    closeButton.addEventListener("click", () => {
      this.closePopup(dialog);
    });

    const copyButton = dialog.querySelector(".email-signup-popup__copy-btn");
    const successMessage = dialog.querySelector(
      ".email-signup-popup__success-message"
    );

    copyButton.addEventListener("click", () => {
      navigator.clipboard.writeText(this.couponCode).then(() => {
        successMessage.style.display = "flex";
        setTimeout(() => {
          successMessage.style.display = "none";
        }, 3000);
      });
    });

    dialog.addEventListener("click", (e) => {
      if (e.target === dialog) {
        this.closePopup(dialog);
      }
    });

    dialog.addEventListener("cancel", () => {
      this.closePopup(dialog);
    });
  }

  closePopup(dialog) {
    dialog.close();
    setTimeout(() => {
      dialog.remove();
    }, 300);
  }

  addStyles() {
    if (document.getElementById("email-signup-popup-styles")) return;

    const style = document.createElement("style");
    style.id = "email-signup-popup-styles";
    style.textContent = `
      .email-signup-popup {
        border: none;
        border-radius: 16px;
        padding: 0;
        max-width: 500px;
        width: 90%;
        box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
        background: transparent;
      }

      .email-signup-popup::backdrop {
        background: rgba(0, 0, 0, 0.5);
        backdrop-filter: blur(4px);
      }

      .email-signup-popup__content {
        background: white;
        padding: 48px 32px 32px;
        border-radius: 16px;
        text-align: center;
        position: relative;
      }

      .email-signup-popup__close {
        position: absolute;
        top: 16px;
        right: 16px;
        background: transparent;
        border: none;
        outline: none;
        cursor: pointer;
        padding: 8px;
        display: flex;
        align-items: center;
        justify-content: center;
        opacity: 0.6;
        transition: opacity 0.2s;
      }

      .email-signup-popup__close:hover {
        opacity: 1;
      }

      .email-signup-popup__title {
        font-family: 'Playfair Display', serif;
        font-size: 32px;
        font-weight: 700;
        margin: 0 0 16px;
        color: #000;
        line-height: 1.2;
      }

      .email-signup-popup__message {
        font-family: 'Lora', serif;
        font-size: 16px;
        line-height: 1.6;
        color: #333;
        margin: 0 0 24px;
      }

      .email-signup-popup__coupon-container {
        background: #f5f5f5;
        border: 2px dashed #ddd;
        border-radius: 8px;
        padding: 16px;
        margin: 0 0 16px;
        display: flex;
        flex-direction: column;
        gap: 12px;
        align-items: center;
      }

      .email-signup-popup__coupon-code {
        font-family: 'Courier New', monospace;
        font-size: 24px;
        font-weight: 700;
        letter-spacing: 2px;
        color: #000;
      }

      .email-signup-popup__copy-btn {
        background: #000;
        color: #fff;
        border: none;
        border-radius: 6px;
        padding: 12px 24px;
        font-size: 14px;
        font-weight: 600;
        cursor: pointer;
        display: flex;
        align-items: center;
        gap: 8px;
        transition: all 0.2s;
      }

      .email-signup-popup__copy-btn:hover {
        background: #333;
        transform: translateY(-1px);
      }

      .email-signup-popup__copy-btn:active {
        transform: translateY(0);
      }

      .email-signup-popup__success-message {
        display: flex;
        align-items: center;
        justify-content: center;
        gap: 8px;
        color: #4CAF50;
        font-size: 14px;
        font-weight: 600;
        margin: 0 0 16px;
      }

      .email-signup-popup__footer {
        font-family: 'Lora', serif;
        font-size: 14px;
        line-height: 1.6;
        color: #666;
        margin: 0;
      }

      @media (max-width: 600px) {
        .email-signup-popup__title {
          font-size: 24px;
        }

        .email-signup-popup__message {
          font-size: 14px;
        }

        .email-signup-popup__coupon-code {
          font-size: 18px;
        }

        .email-signup-popup__content {
          padding: 40px 24px 24px;
        }
      }
    `;
    document.head.appendChild(style);
  }
}

if (!customElements.get("email-signup-block")) {
  customElements.define("email-signup-block", EmailSignupBlock);
}
